import React, { useMemo } from 'react';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { Text } from 'react-native';

import { useAppTheme } from '../contexts/ThemeContext';
import { HomeScreen } from '../screens/main/HomeScreen';
import { SearchScreen } from '../screens/main/SearchScreen';
import { MessagesScreen } from '../screens/main/MessagesScreen';
import { ForumScreen } from '../screens/main/ForumScreen';
import { ForumPostDetailScreen } from '../screens/main/ForumPostDetailScreen';
import { ProfileEditScreen } from '../screens/main/ProfileEditScreen';
import { CommunityNavigator } from './CommunityNavigator';
import { ChatScreen } from '../screens/main/ChatScreen';
import { NewChatScreen } from '../screens/main/NewChatScreen';
import { PlaydatesScreen } from '../screens/main/PlaydatesScreen';
import type { MainStackParamList, MainTabParamList } from './types';
import { navigationThemeFor } from './theme';

const Tab = createBottomTabNavigator<MainTabParamList>();
const Stack = createNativeStackNavigator<MainStackParamList>();

function MainTabs() {
  const { isDark } = useAppTheme();
  const headerBg = isDark ? '#000000' : '#ffffff';
  const headerTint = isDark ? '#fafafa' : '#18181b';
  const tabBg = isDark ? '#000000' : '#ffffff';
  const tabBorder = isDark ? '#27272a' : '#e4e4e7';
  const tabActive = isDark ? '#fafafa' : '#18181b';

  return (
    <Tab.Navigator
      screenOptions={{
        headerShown: true,
        headerStyle: { backgroundColor: headerBg },
        headerTintColor: headerTint,
        headerShadowVisible: false,
        tabBarActiveTintColor: tabActive,
        tabBarInactiveTintColor: '#71717a',
        tabBarLabelStyle: { fontSize: 11, fontWeight: '600' },
        tabBarStyle: {
          backgroundColor: tabBg,
          borderTopColor: tabBorder,
          paddingTop: 4,
          minHeight: 52,
        },
      }}>
      <Tab.Screen
        name="Home"
        component={HomeScreen}
        options={{
          headerShown: false,
          title: 'Ana Sayfa',
          tabBarLabel: 'Ana Sayfa',
          tabBarIcon: () => <Text style={{ fontSize: 18, opacity: 0.9 }}>🏠</Text>,
        }}
      />
      <Tab.Screen
        name="Search"
        component={SearchScreen}
        options={{
          headerShown: false,
          title: 'Annedaş Ara',
          tabBarLabel: 'Arama',
          tabBarIcon: () => <Text style={{ fontSize: 18, opacity: 0.9 }}>🔍</Text>,
        }}
      />
      <Tab.Screen
        name="Messages"
        component={MessagesScreen}
        options={{
          headerShown: false,
          title: 'Mesajlar',
          tabBarLabel: 'Mesajlar',
          tabBarIcon: () => <Text style={{ fontSize: 18, opacity: 0.9 }}>💬</Text>,
        }}
      />
      <Tab.Screen
        name="Forum"
        component={ForumScreen}
        options={{
          headerShown: false,
          title: 'Dert Köşesi',
          tabBarLabel: 'Dert Köşesi',
          tabBarIcon: () => <Text style={{ fontSize: 18, opacity: 0.9 }}>🌸</Text>,
        }}
      />
      <Tab.Screen
        name="Community"
        component={CommunityNavigator}
        options={{
          headerShown: false,
          title: 'Topluluk',
          tabBarLabel: 'Topluluk',
          tabBarIcon: () => <Text style={{ fontSize: 18, opacity: 0.9 }}>🤝</Text>,
        }}
      />
    </Tab.Navigator>
  );
}

export function MainNavigator() {
  const { isDark } = useAppTheme();
  const navTheme = useMemo(() => navigationThemeFor(isDark), [isDark]);
  const stackScreenOptions = useMemo(
    () => ({
      headerStyle: { backgroundColor: isDark ? '#000000' : '#ffffff' },
      headerTintColor: isDark ? '#fafafa' : '#18181b',
      headerShadowVisible: false,
      headerTitleStyle: { fontWeight: '600' as const },
      /** iOS: önceki route adı (ör. MainTabs) geri yazısında görünmesin — yalnızca chevron. */
      headerBackButtonDisplayMode: 'minimal' as const,
    }),
    [isDark],
  );

  return (
    <Stack.Navigator screenOptions={stackScreenOptions}>
      <Stack.Screen
        name="MainTabs"
        component={MainTabs}
        options={{
          headerShown: false,
          /** Geri etiketi için kullanılır; route adı yerine uygulama adı. */
          title: 'Annedaş',
        }}
      />
      <Stack.Screen
        name="NewChat"
        component={NewChatScreen}
        options={{
          title: 'Yeni mesaj',
          presentation: 'modal',
          contentStyle: { backgroundColor: navTheme.colors.background },
        }}
      />
      <Stack.Screen
        name="Chat"
        component={ChatScreen}
        options={({ route }) => ({
          title: route.params.peerName,
          contentStyle: { backgroundColor: navTheme.colors.background },
        })}
      />
      <Stack.Screen
        name="ForumPostDetail"
        component={ForumPostDetailScreen}
        options={{
          title: 'Gönderi',
          contentStyle: { backgroundColor: navTheme.colors.background },
        }}
      />
      <Stack.Screen
        name="ProfileEdit"
        component={ProfileEditScreen}
        options={{
          title: 'Profilini düzenle',
          presentation: 'modal',
          contentStyle: { backgroundColor: navTheme.colors.background },
        }}
      />
      <Stack.Screen
        name="Playdates"
        component={PlaydatesScreen}
        options={{
          title: 'Oyun Arkadaşı',
          contentStyle: { backgroundColor: navTheme.colors.background },
        }}
      />
    </Stack.Navigator>
  );
}
